import React, { useState } from "react";
import { Copy, Check, Palette } from "lucide-react";

export default function App() {
  const [selectedColor, setSelectedColor] = useState("#ff0080");
  const [copied, setCopied] = useState(false);
  const [hue, setHue] = useState(330);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);

  const rainbowColors = [
    "#FF0000",
    "#FF7F00",
    "#FFFF00",
    "#00FF00",
    "#0000FF",
    "#4B0082",
    "#9400D3",
    "#FF1493",
    "#FF69B4",
    "#00CED1",
    "#32CD32",
    "#FFD700",
  ];

  const updateColorFromHSL = (h, s, l) => {
    const rgb = hslToHex(h, s, l);
    setSelectedColor(rgb);
  };

  const hslToHex = (h, s, l) => {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };

  const handleHueChange = (e) => {
    const newHue = parseInt(e.target.value);
    setHue(newHue);
    updateColorFromHSL(newHue, saturation, lightness);
  };

  const handleSaturationChange = (e) => {
    const newSat = parseInt(e.target.value);
    setSaturation(newSat);
    updateColorFromHSL(hue, newSat, lightness);
  };

  const handleLightnessChange = (e) => {
    const newLight = parseInt(e.target.value);
    setLightness(newLight);
    updateColorFromHSL(hue, saturation, newLight);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(selectedColor);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500 p-8 flex items-center justify-center">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8 animate-pulse">
          <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-lg rounded-full px-6 py-3 mb-4">
            <Palette className="w-8 h-8 text-white" />
            <h1 className="text-4xl font-bold text-white">Rainbow Picker</h1>
          </div>
          <p className="text-white/90 text-lg">
            Choose your perfect color from the spectrum
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 space-y-6">
          <div className="relative">
            <div
              className="w-full h-48 rounded-2xl shadow-lg transition-all duration-300 border-4 border-white"
              style={{ backgroundColor: selectedColor }}
            />
            <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg">
              <p className="text-2xl font-bold text-gray-800">
                {selectedColor.toUpperCase()}
              </p>
            </div>
          </div>

          <button
            onClick={copyToClipboard}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            {copied ? (
              <>
                <Check className="w-5 h-5" />
                Copied to Clipboard!
              </>
            ) : (
              <>
                <Copy className="w-5 h-5" />
                Copy Color Code
              </>
            )}
          </button>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Hue: {hue}°
              </label>
              <input
                type="range"
                min="0"
                max="360"
                value={hue}
                onChange={handleHueChange}
                className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                style={{
                  background:
                    "linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)",
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Saturation: {saturation}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={saturation}
                onChange={handleSaturationChange}
                className="w-full h-3 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-gray-400 to-pink-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Lightness: {lightness}%
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={lightness}
                onChange={handleLightnessChange}
                className="w-full h-3 rounded-lg appearance-none cursor-pointer bg-gradient-to-r from-black via-gray-500 to-white"
              />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Rainbow Presets
            </h3>
            <div className="grid grid-cols-6 gap-3">
              {rainbowColors.map((color, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedColor(color)}
                  className="w-full h-14 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-110 border-2 border-white"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-700">
              Custom:
            </label>
            <input
              type="color"
              value={selectedColor}
              onChange={(e) => setSelectedColor(e.target.value)}
              className="w-20 h-12 rounded-lg cursor-pointer border-2 border-gray-300"
            />
            <input
              type="text"
              value={selectedColor}
              onChange={(e) => setSelectedColor(e.target.value)}
              className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none font-mono"
              placeholder="#000000"
            />
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-white/80 text-sm">Made with 🌈 and React</p>
        </div>
      </div>
    </div>
  );
}
